import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiX, FiSave, FiBriefcase, FiUser, FiMapPin } from 'react-icons/fi';
import Button from '../../components/ui/Button';
import ErrorBanner from '../../components/ui/ErrorBanner';
import CurrencySelect from '../../components/shared/CurrencySelect';
import CountrySelect from '../../components/shared/CountrySelect';
import { saveCustomer } from '../../api/customers';
import { extractErrorMessage } from '../../api/client';
import { getCountries, getCurrencies } from '../../api/reference';

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 bg-slate-50 border-b border-slate-200">
        <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-primary">
          <Icon size={13} />
        </div>
        <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-[0.05em] mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1 font-medium">{error}</p>}
    </div>
  );
}

export default function CustomerDetailDrawer({ open, onClose, customer, onSuccess }) {
  const qc = useQueryClient();
  const isEditing = !!(customer && customer.id);

  // — Company —
  const [companyName, setCompanyName]   = useState('');
  const [gstNumber, setGstNumber]       = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [billingCountry, setBillingCountry] = useState('IN');
  const [currency, setCurrency]         = useState('INR');

  // — Contact Person —
  const [contactPerson, setContactPerson]   = useState('');
  const [email, setEmail]                   = useState('');
  const [phone, setPhone]                   = useState('');
  const [alternatePhone, setAlternatePhone] = useState('');

  // — Billing Address —
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity]                 = useState('');
  const [billingState, setBillingState]               = useState('');
  const [billingPostalCode, setBillingPostalCode]     = useState('');

  // — Shipping Address —
  const [shippingAddressLine1, setShippingAddressLine1] = useState('');
  const [shippingAddressLine2, setShippingAddressLine2] = useState('');
  const [shippingCity, setShippingCity]                 = useState('');
  const [shippingState, setShippingState]               = useState('');
  const [shippingPostalCode, setShippingPostalCode]     = useState('');
  const [shippingCountry, setShippingCountry]           = useState('IN');
  const [copyBillingToShipping, setCopyBillingToShipping] = useState(false);

  const [errors, setErrors]           = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Track manual currency override — start as true when editing so existing
  // currency is preserved on load
  const userOverrideCurrency = useRef(isEditing);

  // Reference data
  const { data: countries = [] } = useQuery({
    queryKey: ['reference', 'countries'],
    queryFn: getCountries,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const { data: currencyList = [] } = useQuery({
    queryKey: ['reference', 'currencies'],
    queryFn: getCurrencies,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const orderedCurrencies = useMemo(() => {
    const order = { USD: 1, GBP: 2, EUR: 3, INR: 4 };
    return [...currencyList].sort((a, b) => {
      const oA = order[a.code] || 99;
      const oB = order[b.code] || 99;
      if (oA !== oB) return oA - oB;
      return a.code.localeCompare(b.code);
    });
  }, [currencyList]);

  // Country → default currency map
  const countryCurrencyMap = useMemo(() => {
    const fromApi = {};
    countries.forEach((c) => { if (c.currencyCode) fromApi[c.code] = c.currencyCode; });
    return {
      IN: 'INR', US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD',
      SG: 'SGD', CH: 'CHF', JP: 'JPY', CN: 'CNY', AE: 'AED', NZ: 'NZD',
      SA: 'SAR', KW: 'KWD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP',
      KR: 'KRW', BR: 'BRL', MX: 'MXN', ZA: 'ZAR', RU: 'RUB', TR: 'TRY',
      SE: 'SEK', NO: 'NOK', DK: 'DKK', HK: 'HKD', TW: 'TWD', VN: 'VND',
      QA: 'QAR', OM: 'OMR', BH: 'BHD', IL: 'ILS', EG: 'EGP', NG: 'NGN',
      PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR', DE: 'EUR', FR: 'EUR',
      IT: 'EUR', ES: 'EUR', NL: 'EUR', PT: 'EUR', BE: 'EUR', AT: 'EUR',
      ...fromApi,
    };
  }, [countries]);

  // Auto-set currency when country changes (unless user overrode it).
  // Note: the flag is reset to false inside the country onChange handler so that
  // manually changing the country dropdown always triggers auto-select.
  useEffect(() => {
    if (userOverrideCurrency.current) return;
    const mapped = countryCurrencyMap[billingCountry];
    if (mapped) setCurrency(mapped);
  }, [billingCountry, countryCurrencyMap]);

  // Populate form when opening
  useEffect(() => {
    if (!open) return;
    const c = customer;
    // When editing an existing customer, protect the saved currency initially.
    // The country onChange handler will clear this flag when the user manually
    // changes the country, so auto-select still works after that point.
    userOverrideCurrency.current = !!c;

    setCompanyName(c?.companyName || '');
    setGstNumber(c?.gstNumber || '');
    setPaymentTerms(c?.paymentTerms || '');
    setBillingCountry(c?.billingCountry || 'IN');
    setCurrency(c?.currency || 'INR');

    setContactPerson(c?.contactPerson || '');
    setEmail(c?.email || '');
    setPhone(c?.phone || '');
    setAlternatePhone(c?.alternatePhone || '');

    setBillingAddressLine1(c?.billingAddressLine1 || '');
    setBillingAddressLine2(c?.billingAddressLine2 || '');
    setBillingCity(c?.billingCity || '');
    setBillingState(c?.billingState || '');
    setBillingPostalCode(c?.billingPostalCode || '');

    setShippingAddressLine1(c?.shippingAddressLine1 || '');
    setShippingAddressLine2(c?.shippingAddressLine2 || '');
    setShippingCity(c?.shippingCity || '');
    setShippingState(c?.shippingState || '');
    setShippingPostalCode(c?.shippingPostalCode || '');
    setShippingCountry(c?.shippingCountry || 'IN');
    setCopyBillingToShipping(false);
    setErrors({});
    setSubmitError(null);
  }, [open, customer]);

  // Copy billing → shipping
  useEffect(() => {
    if (!copyBillingToShipping) return;
    setShippingAddressLine1(billingAddressLine1);
    setShippingAddressLine2(billingAddressLine2);
    setShippingCity(billingCity);
    setShippingState(billingState);
    setShippingPostalCode(billingPostalCode);
    setShippingCountry(billingCountry);
  }, [copyBillingToShipping, billingAddressLine1, billingAddressLine2, billingCity, billingState, billingPostalCode, billingCountry]);

  const mutation = useMutation({
    mutationFn: saveCustomer,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(isEditing ? 'Client updated' : 'Client created');
      onSuccess?.(data);
      onClose();
    },
    onError: (err) => {
      setSubmitError(err);
      toast.error(extractErrorMessage(err));
    },
  });

  const validate = () => {
    const errs = {};
    if (!companyName.trim()) errs.companyName = 'Company name is required';
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Enter a valid email address';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) { toast.error('Please fix the errors'); return; }
    mutation.mutate({
      ...(isEditing ? { id: customer.id } : {}),
      customerType: 'BUSINESS',
      companyName: companyName.trim() || null,
      gstNumber: gstNumber.trim() || null,
      paymentTerms: paymentTerms.trim() || null,
      billingCountry: billingCountry || null,
      currency: currency || null,
      contactPerson: contactPerson.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      alternatePhone: alternatePhone.trim() || null,
      billingAddressLine1: billingAddressLine1.trim() || null,
      billingAddressLine2: billingAddressLine2.trim() || null,
      billingCity: billingCity.trim() || null,
      billingState: billingState.trim() || null,
      billingPostalCode: billingPostalCode.trim() || null,
      shippingAddressLine1: shippingAddressLine1.trim() || null,
      shippingAddressLine2: shippingAddressLine2.trim() || null,
      shippingCity: shippingCity.trim() || null,
      shippingState: shippingState.trim() || null,
      shippingPostalCode: shippingPostalCode.trim() || null,
      shippingCountry: shippingCountry || null,
    });
  };

  const inputCls = 'w-full h-[34px] border border-slate-200 rounded-lg bg-white text-slate-900 text-[13px] px-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors';
  const selectCls = `${inputCls} cursor-pointer`;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[59] bg-slate-900/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-[60] flex w-full max-w-2xl flex-col bg-slate-50 shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              {isEditing ? 'Edit Client' : 'Add New Client'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEditing ? 'Update client information' : 'Fill in the details to create a new client'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <form id="customer-form" onSubmit={handleSubmit} className="space-y-4">
            {submitError && <ErrorBanner error={submitError} />}

            {/* ── Company Details ── */}
            <SectionCard icon={FiBriefcase} title="Company Details">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Field label="Company Name" required error={errors.companyName}>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g. Acme Corporation"
                      className={`${inputCls} ${errors.companyName ? 'border-red-400 focus:border-red-400' : ''}`}
                    />
                  </Field>
                </div>
                <Field label="GST Number">
                  <input
                    type="text"
                    value={gstNumber}
                    onChange={(e) => setGstNumber(e.target.value)}
                    placeholder="22AAAAA0000A1Z5"
                    className={inputCls}
                  />
                </Field>
                <Field label="Payment Terms">
                  <select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={selectCls}>
                    <option value="">Select terms...</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                </Field>
                {/* Country first, then Currency (auto-fills from country) */}
                <Field label="Country">
                  <CountrySelect
                    countries={countries}
                    value={billingCountry}
                    onChange={(code) => {
                      userOverrideCurrency.current = false; // allow auto-fill
                      setBillingCountry(code);
                    }}
                  />
                </Field>
                <Field label="Currency">
                  <CurrencySelect
                    currencies={orderedCurrencies}
                    value={currency}
                    onChange={(code) => {
                      userOverrideCurrency.current = true; // manual override
                      setCurrency(code);
                    }}
                  />
                </Field>
              </div>
            </SectionCard>

            {/* ── Contact Person ── */}
            <SectionCard icon={FiUser} title="Contact Person">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Field label="Contact Person Name">
                    <input
                      type="text"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                      placeholder="e.g. John Smith"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="Email" required error={errors.email}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@company.com"
                      className={`${inputCls} ${errors.email ? 'border-red-400 focus:border-red-400' : ''}`}
                    />
                  </Field>
                </div>
                <Field label="Phone">
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className={inputCls}
                  />
                </Field>
                <Field label="Alternate Phone">
                  <input
                    type="text"
                    value={alternatePhone}
                    onChange={(e) => setAlternatePhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className={inputCls}
                  />
                </Field>
              </div>
            </SectionCard>

            {/* ── Billing Address ── */}
            <SectionCard icon={FiMapPin} title="Billing Address">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Field label="Address Line 1">
                    <input type="text" value={billingAddressLine1} onChange={(e) => setBillingAddressLine1(e.target.value)} placeholder="Street address" className={inputCls} />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="Address Line 2">
                    <input type="text" value={billingAddressLine2} onChange={(e) => setBillingAddressLine2(e.target.value)} placeholder="Apt, suite, etc." className={inputCls} />
                  </Field>
                </div>
                <Field label="City">
                  <input type="text" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} placeholder="Mumbai" className={inputCls} />
                </Field>
                <Field label="State">
                  <input type="text" value={billingState} onChange={(e) => setBillingState(e.target.value)} placeholder="Maharashtra" className={inputCls} />
                </Field>
                <Field label="Postal Code">
                  <input type="text" value={billingPostalCode} onChange={(e) => setBillingPostalCode(e.target.value)} placeholder="400001" className={inputCls} />
                </Field>
              </div>
            </SectionCard>

            {/* ── Shipping Address ── */}
            <SectionCard icon={FiMapPin} title="Shipping Address">
              <div className="mb-4">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <div
                    onClick={() => setCopyBillingToShipping(!copyBillingToShipping)}
                    className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${copyBillingToShipping ? 'bg-primary' : 'bg-slate-300'}`}
                  >
                    <div className={`absolute w-3.5 h-3.5 top-0.5 rounded-full bg-white shadow transition-transform ${copyBillingToShipping ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-[12px] font-medium text-slate-600">Same as billing address</span>
                </label>
              </div>
              {!copyBillingToShipping && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Field label="Address Line 1">
                      <input type="text" value={shippingAddressLine1} onChange={(e) => setShippingAddressLine1(e.target.value)} placeholder="Street address" className={inputCls} />
                    </Field>
                  </div>
                  <div className="col-span-2">
                    <Field label="Address Line 2">
                      <input type="text" value={shippingAddressLine2} onChange={(e) => setShippingAddressLine2(e.target.value)} placeholder="Apt, suite, etc." className={inputCls} />
                    </Field>
                  </div>
                  <Field label="City">
                    <input type="text" value={shippingCity} onChange={(e) => setShippingCity(e.target.value)} placeholder="Mumbai" className={inputCls} />
                  </Field>
                  <Field label="State">
                    <input type="text" value={shippingState} onChange={(e) => setShippingState(e.target.value)} placeholder="Maharashtra" className={inputCls} />
                  </Field>
                  <Field label="Postal Code">
                    <input type="text" value={shippingPostalCode} onChange={(e) => setShippingPostalCode(e.target.value)} placeholder="400001" className={inputCls} />
                  </Field>
                  <Field label="Country">
                    <CountrySelect
                      countries={countries}
                      value={shippingCountry}
                      onChange={(code) => setShippingCountry(code)}
                    />
                  </Field>
                </div>
              )}
            </SectionCard>
          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-200 bg-white px-6 py-4 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            form="customer-form"
            type="submit"
            variant="primary"
            icon={FiSave}
            loading={mutation.isPending}
          >
            {isEditing ? 'Save Changes' : 'Create Client'}
          </Button>
        </div>
      </div>
    </>
  );
}
